import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { getPosts } from "@/lib/api";
import { POST_CATEGORIES, categoryStyle } from "@/lib/constants";

export default function CategoriesPage() {
    const [posts, setPosts] = useState([]);
    const [active, setActive] = useState("All");

    useEffect(() => {
        Promise.all([getPosts("featured"), getPosts("recent"), getPosts("latest")])
            .then(([a, b, c]) => {
                const all = [...a, ...b, ...c];
                const dedup = Array.from(
                    new Map(all.map((p) => [p.id, p])).values(),
                );
                // Newest first, chronological.
                dedup.sort((x, y) => (y.date || "").localeCompare(x.date || ""));
                setPosts(dedup);
            })
            .catch(() => setPosts([]));
    }, []);

    const counts = useMemo(() => {
        const m = {};
        posts.forEach((p) => {
            m[p.category] = (m[p.category] || 0) + 1;
        });
        return m;
    }, [posts]);

    const filtered = useMemo(
        () => (active === "All" ? posts : posts.filter((p) => p.category === active)),
        [posts, active],
    );

    return (
        <div data-testid="categories-page">
            <PageHero
                eyebrow="Browse"
                title="Categories"
                lead="Every category we publish. Pick one to filter the entire archive."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Categories" },
                ]}
            />

            <div className="px-4 md:px-8 lg:px-12 py-12">
                <div className="flex flex-wrap gap-2 mb-10" data-testid="category-chips">
                    <button
                        onClick={() => setActive("All")}
                        className={`badge-category transition ${
                            active === "All" ? "ring-2 ring-offset-2 ring-[#1B1B1B]" : ""
                        }`}
                        style={{ background: "#1B1B1B", color: "#F5C53D" }}
                        data-testid="category-chip-all"
                    >
                        All ({posts.length})
                    </button>
                    {POST_CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActive(c)}
                            className={`badge-category transition ${
                                active === c ? "ring-2 ring-offset-2 ring-[#1B1B1B]" : ""
                            }`}
                            style={categoryStyle(c)}
                            data-testid={`category-chip-${c.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                        >
                            {c} ({counts[c] || 0})
                        </button>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <p className="font-sofia text-[#1B1B1B]/60 text-center py-10">
                        No posts in this category yet. Check back soon.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((p) => (
                            <Link
                                key={p.id}
                                to={`/post/${p.slug}`}
                                className="post-card group block border border-black/10 hover:border-[#E63B26] transition overflow-hidden"
                                data-testid={`category-post-${p.slug}`}
                            >
                                <div className="aspect-[3/2] overflow-hidden bg-[#1B1B1B]">
                                    <img
                                        src={p.image_url}
                                        alt={p.title}
                                        className="post-card-image w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-4">
                                    <span
                                        className="badge-category text-[0.65rem]"
                                        style={categoryStyle(p.category)}
                                    >
                                        {p.category}
                                    </span>
                                    <h3 className="mt-3 font-poppins font-bold text-lg leading-tight group-hover:text-[#E63B26] transition">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 font-sofia text-sm text-[#1B1B1B]/70 line-clamp-3">
                                        {p.blurb}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
